/**
 * Helpers para manejo de audio cross-browser
 */

import { detectBrowser } from './browserDetection';
import { getAudioContext } from './polyfills';

export interface AudioRecorderOptions {
  mimeType?: string;
  audioBitsPerSecond?: number;
}

export class AudioRecorderHelper {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;

  async startRecording(): Promise<void> {
    try {
      const browserInfo = detectBrowser();
      
      // Solicitar permisos de micrófono
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Determinar el tipo MIME soportado
      let mimeType = 'audio/webm';
      if (browserInfo.isSafariIOS || browserInfo.isSafari) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/ogg')) {
        mimeType = 'audio/ogg';
      }

      // Crear MediaRecorder
      const options: AudioRecorderOptions = {
        mimeType,
        audioBitsPerSecond: 128000,
      };

      this.mediaRecorder = new MediaRecorder(this.stream, options);
      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.start();
    } catch (error) {
      console.error('Error al iniciar grabación:', error);
      throw error;
    }
  }

  async stopRecording(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        reject(new Error('No hay grabación activa'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: this.mediaRecorder?.mimeType || 'audio/webm' });
        this.cleanup();
        resolve(audioBlob);
      };

      this.mediaRecorder.onerror = (error) => {
        this.cleanup();
        reject(error);
      };

      this.mediaRecorder.stop();
    });
  }

  private cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    this.mediaRecorder = null;
    this.audioChunks = [];
  }

  async convertToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = (reader.result as string).split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}

export function initializeAudioContext(): AudioContext | null {
  const audioContext = getAudioContext();
  
  if (audioContext && audioContext.state === 'suspended') {
    // En algunos navegadores, el AudioContext se suspende hasta que hay interacción del usuario
    audioContext.resume().catch(err => {
      console.warn('No se pudo reanudar AudioContext:', err);
    });
  }

  return audioContext;
}


